import useSWR from 'swr';
import { useEffect } from 'react';
import { supabase, retryOperation } from '../utils/supabase';
import { Product } from '../types';

const fetcher = async () => {
  return retryOperation(async () => {
    try {
      // Always fetch directly from Supabase with freshest data first
      const { data, error } = await supabase
        .from('products')
        .select('*') // Select all fields including category
        .eq('is_published', true)
        .eq('is_deleted', false)
        .order('updated_at', { ascending: false }); // Order by updated_at DESC for freshest data

      if (error) throw error;
      
      console.log('Fetched products ordered by updated_at:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('Error fetching products:', error);
      if (error.message?.includes('Failed to fetch')) {
        throw new Error('Network error: Unable to connect to the database. Please check your internet connection.');
      }
      throw error;
    }
  }, 3, 1000); // 3 retries with 1s initial delay
};

export function useProducts() {
  const { data, error, mutate } = useSWR<Product[]>('public-products', fetcher, {
    refreshInterval: 0, // Disable automatic refresh - rely on real-time subscriptions
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    dedupingInterval: 1000, // Reduce deduping to allow more frequent updates
    errorRetryCount: 3,
    onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
      // Don't retry on 404s
      if (error.status === 404) return;

      // Don't retry if we've hit our retry limit
      if (retryCount >= 3) return;

      // Exponential backoff
      setTimeout(() => revalidate({ retryCount }), 
        Math.min(1000 * Math.pow(2, retryCount), 30000)
      );
    }
  });

  useEffect(() => {
    const subscription = supabase
      .channel('public_products')
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'products',
          filter: 'is_published=eq.true'
        },
        (payload) => {
          console.log('Real-time product change detected:', payload);
          mutate(); // Re-fetch products when changes occur
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [mutate]);

  return {
    products: data || [],
    isLoading: !error && !data,
    isError: !!error,
    error: error, // Expose the error for better error handling in components
    mutate
  };
}